<template>
  <div v-if="tradein">
    <div class="smaller-container-wrapper">
      <h2 :class="{ 'pt-3 pt-sm-4': currentSubFlow === 'trade-in' }" class="mb-4 mb-sm-5">
        {{ getVehicleName }}
        <br />
        - {{ $n(vehicle.mileage, 'number', languageCode) }} {{ $t('message.eCommerceV2.tradeInFlow.km') }}
      </h2>
      <CarfaxLogoContainer v-if="tradeInSourceIsCarfax" />
      <hr v-else />
      <div class="px-sm-5">
        <p class="clearfix mt-4 mt-sm-5">
          <span>{{ $t('message.eCommerceV2.tradeInFlow.results.estimatedValue') }}</span>
          <span class="float-right">{{ $n(estimatedValue, 'currencyDollars', languageCode) }}</span>
        </p>
        <p class="clearfix pb-sm-3">
          <span>{{ $t('message.eCommerceV2.tradeInFlow.results.outstandingLoan') }}</span>
          <span class="float-right">{{ $n(outstandingTradeInBalance, 'currencyDollars', languageCode) }}</span>
        </p>
        <p class="clearfix mt-4">
          <span class="lead font-weight-bold text-dark">{{
            $t('message.eCommerceV2.tradeInFlow.results.netTrade')
          }}</span>
          <span class="float-right h3 mt-0 text-dark">{{ $n(netTradeValue, 'currencyDollars', languageCode) }}</span>
        </p>
        <p v-if="averageTaxSavingValue" class="font-italic mt-4">
          {{
            $t('message.eCommerceV2.tradeInFlow.results.taxSavings', {
              taxAmount: $n(this.averageTaxSavingValue, 'currencyDollars', languageCode),
            })
          }}
        </p>
        <div class="row mt-5 form-row">
          <div class="col-sm-6 order-sm-2">
            <mobialis-button
              class="mb-3"
              @click="applyToDeal"
              data-cy="applyToDeal"
              v-av-analytics-cta-interaction="{ elementText: buttonText, eventActionResult: 'open' }"
              >{{ buttonText }}</mobialis-button
            >
          </div>
          <div class="col-sm-6 order-sm-1">
            <mobialis-button
              @click="removeTradeIn"
              variant="secondary"
              data-cy="removeTrade"
              v-av-analytics-cta-interaction="{
                elementText: $t('message.eCommerceV2.tradeInFlow.results.removeTrade'),
                eventActionResult: 'open',
              }"
              >{{ $t('message.eCommerceV2.tradeInFlow.results.removeTrade') }}</mobialis-button
            >
          </div>
        </div>
      </div>
    </div>
    <div class="bottom-section mt-5">
      <div class="smaller-container-wrapper py-4 py-sm-5">
        <p class="small">{{ $t('message.eCommerceV2.tradeInFlow.results.valueBasedOn') }}</p>
        <p class="small mt-4">
          {{ $t('message.eCommerceV2.tradeInFlow.results.valueBasedOn2', { dealership: getBusinessName }) }}
        </p>
        <p v-if="averageTaxSavingValue" class="small mt-4">
          {{
            $t('message.eCommerceV2.tradeInFlow.results.taxInfo', {
              taxRate: $n(this.tradein.tax_rate / 100, 'percent', languageCode),
            })
          }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { enumMixin } from '@/mixins';
import { inDollars } from '@/utils/numbers';
import CarfaxLogoContainer from '@/components/shared/trade-in-carfax/CarfaxLogoContainer.vue';
import TradeInSource from '@/utils/enums/tradeInSource';

export default {
  name: 'TradeInResults',
  components: { CarfaxLogoContainer },
  mixins: [enumMixin],
  computed: {
    ...mapState('trade', ['tradein', 'vehicle']),
    ...mapState('paymentCalculator', ['tradeInValue', 'outstandingTradeInBalance']),
    ...mapState('route', ['from']),
    ...mapGetters({
      languageCode: 'user/getLanguageCodeForNumericalInternationalization',
      getVehicleName: 'trade/getVehicleName',
      getBusinessName: 'business/getBusinessName',
    }),
    estimatedValue() {
      return (inDollars(this.tradein.trade_in_value.high_value) + inDollars(this.tradein.trade_in_value.low_value)) / 2;
    },
    averageTaxSavingValue() {
      return +this.tradein.tax_rate ? this.estimatedValue * (parseFloat(this.tradein.tax_rate) / 100) : null;
    },
    netTradeValue() {
      return this.estimatedValue - this.outstandingTradeInBalance;
    },
    buttonText() {
      if (this.from.path.includes('payment-calculator') || this.from.path.includes('payment-summary')) {
        return this.$t('message.eCommerceV2.tradeInFlow.results.backToDeal');
      }
      return this.$t('message.eCommerceV2.tradeInFlow.results.applyToDeal');
    },
    tradeInSourceIsCarfax() {
      return this.$store.getters['business/getTradeInSource'] === TradeInSource.CARFAX;
    },
  },
  methods: {
    applyToDeal() {
      this.$store.commit('paymentCalculator/SET_STATE', ['tradeInValue', this.estimatedValue]);
      this.$store.commit('paymentCalculator/SET_STATE', ['hasTradeIn', this.enums.YesNo.YES]);
      this.$router.push({ path: this.getNextRoutePath() });
    },
    removeTradeIn() {
      // Remove all data associated with the trade in for use in the UI
      // Keep what is attached to the lead state.updateLeadVehicles.tradein
      // for tracking and data collection purposes
      this.$store.commit('trade/RESET_VEHICLE');
      this.$store.commit('paymentCalculator/REMOVE_TRADE');
      this.$router.push({ path: this.getNextRoutePath() });
    },
  },
};
</script>

<style lang="scss" scoped>
.bottom-section {
  background-color: #f0f4f7;
}
.small {
  font-size: 12px;
  line-height: 18px;
}
@media (min-width: 576px) {
  h2 {
    text-align: center;
  }
}
</style>
